import { MouseEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Sticky from 'react-sticky-el';
import * as Collapsible from '@radix-ui/react-collapsible';
import classNames from 'classnames';
import isEmpty from 'lodash-es/isEmpty';

import { icon_burger, icon_close_medium } from '@gn-audio/icons';
import {
  Button,
  JabraIcon,
  useBreakpoint,
  useFallbackTranslation,
  useOutsideAlerter,
} from '@gn-audio/jss-components';

import { CondensedMenuLink } from 'common/condensedMenu/CondensedMenuLink';
import { useCondensedMenu } from 'common/condensedMenu/CondensedMenuProvider';
import { HideOnScrollDown, useScrollDirection } from 'common/condensedMenu/HideOnScrollDown';
import { useScrollToItem } from 'common/condensedMenu/scrollToItem';
import { useHash } from 'common/condensedMenu/UseHash';
import { ProductPageBuyButton } from 'common/PageProductContext/ProductPageBuyButton';
import { ProductPagePrice } from 'common/PageProductContext/ProductPagePrice';

import styles from './CondensedMenu.module.scss';

export const CondensedMenuComponent = () => {
  const { t } = useFallbackTranslation();
  const [open, setOpen] = useState(false);
  const { menuItems, showMenu, settings } = useCondensedMenu();
  const [initialized, setInitialized] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const hash = useHash();
  const { isAboveLg } = useBreakpoint('lg', true);
  const scrollDirection = useScrollDirection();
  const { scrollTo } = useScrollToItem();

  const timeoutRef = useRef<any | null>(null);
  useEffect(() => {
    if (initialized) return;
    if (isEmpty(hash)) {
      setInitialized(true);
      return;
    }
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      scrollTo(hash.replace('_', ''), 'smooth').then(() => {
        setInitialized(true);
      });
    }, 600);
  }, [hash, initialized, scrollTo]);
  const closeMenu = () => {
    setOpen(false);
  };
  useOutsideAlerter(menuRef, closeMenu);

  useEffect(() => {
    if (open && scrollDirection === 'down') {
      setOpen(false);
    }
  }, [open, scrollDirection]);

  const handleScrollTo = (e: MouseEvent<HTMLButtonElement>, anchor: string) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);

    scrollTo(anchor, 'smooth').then(() => {
      // Move focus into the section so Tab continues from here, not the menu item.
      document.getElementById(anchor)?.focus({ preventScroll: true });
    });
  };

  if (!showMenu) {
    return null;
  }

  return (
    <StickyPortal>
      <HideOnScrollDown enabled={!open || isAboveLg}>
        <div
          className={classNames(
            styles.condensedMenu,
            'dark flex h-[4.25rem] w-full items-center gap-4 px-6 text-white max-lg:hidden'
          )}
        >
          <ul className="flex flex-1 gap-6">
            {menuItems
              .filter((menuItem) => menuItem.name)
              .map((menuItem) => (
                <li key={menuItem.anchor}>
                  <button
                    className="py-1 hover:underline"
                    onClick={(e) => handleScrollTo(e, menuItem.anchor)}
                  >
                    {menuItem.name}
                  </button>
                </li>
              ))}
          </ul>
          {settings.showBuyProductAction && (
            <div className="flex items-center gap-6">
              <ProductPagePrice />
              <ProductPageBuyButton />
            </div>
          )}
          {settings.showLink && <CondensedMenuLink anchorAction={handleScrollTo} />}
        </div>

        <div
          ref={menuRef}
          className={classNames('dark lg:hidden', open && styles.condensedMobileMenuOpened)}
        >
          <Collapsible.Root open={open} onOpenChange={setOpen}>
            <div
              className={classNames(
                !open && styles.condensedMenu,
                'flex h-[4.25rem] w-full items-center justify-between gap-6 px-6 text-white'
              )}
            >
              <Collapsible.Trigger asChild>
                <button
                  title={t('system.Navigation') || 'Navigation'}
                  aria-haspopup="menu"
                  aria-expanded="false"
                  className="btn-top-menu"
                >
                  {!open && <JabraIcon Icon={icon_burger} />}
                  {open && <JabraIcon Icon={icon_close_medium} />}
                </button>
              </Collapsible.Trigger>
              <div className="flex items-center gap-4">
                {settings.showBuyProductAction && <ProductPageBuyButton />}
                {settings.showLink && <CondensedMenuLink anchorAction={handleScrollTo} />}
              </div>
            </div>
            <Collapsible.Content className="collapsible-content">
              <ul className="flex flex-col items-start gap-6 pb-6">
                {menuItems.map((menuItem) => {
                  return (
                    <li key={menuItem.anchor}>
                      <Button
                        className="[&_div]:font-normal"
                        asLink
                        linkUnderline={true}
                        variant="quaternary"
                        size="m"
                        onClick={(e) => handleScrollTo(e, menuItem.anchor)}
                      >
                        {menuItem.name}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </Collapsible.Content>
          </Collapsible.Root>
        </div>
      </HideOnScrollDown>
    </StickyPortal>
  );
};

const StickyPortal = ({ children }: { children: ReactNode }) => {
  const { t } = useFallbackTranslation();
  const [isFixed, setIsFixed] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted || typeof document === 'undefined') {
    return null;
  }
  const container = document.getElementById('page-container-wrapper');

  if (!container) {
    return null;
  }

  return (
    <>
      <nav aria-label={`${t('system.CondensedMenu')}`} className="relative h-0 w-full">
        {children}
      </nav>
      {createPortal(
        <div className="max-lg:absolute max-lg:top-0 max-lg:w-full">
          <Sticky onFixedToggle={setIsFixed}>
            <div className={classNames(!isFixed && 'hidden')}>
              <nav
                aria-label={`${t('system.StickyCondensedMenu')}`}
                className="relative h-0 w-full"
              >
                {children}
              </nav>
            </div>
          </Sticky>
        </div>,
        container
      )}
    </>
  );
};
